import { Alert, Input, Modal, Spin, Typography, message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createCheckout, getPublicPlans, previewBillingPlans, reconcilePaymentStatus } from '../api';
import CheckoutPaymentModal from '../components/CheckoutPaymentModal';
import { API_BASE_URL } from '../api/baseUrl';
import { useAuth } from '../auth/AuthContext';
import { formatCreditEstimateSummary, formatCreditGrantText } from '../utils/creditEstimate';
import { resolveErrorMessage } from '../utils/errorMessage';
import { renderWxLogin } from '../utils/wxLogin';
import './LandingPage.css';

const PLATFORM_ITEMS = [
  '微信视频号',
  '小红书',
  '抖音',
  '快手',
];

const PLATFORM_TRACK_REPEAT = 6;
const PLATFORM_TRACK_ITEMS = Array.from({ length: PLATFORM_TRACK_REPEAT }).flatMap(() => PLATFORM_ITEMS);

const WORKFLOW_STEPS = [
  {
    step: '01',
    title: '生产内容',
    desc: '围绕选题、原始素材或已有内容，快速生成图文、短视频脚本和不同平台可继续编辑的草稿。',
    points: ['生成平台适配版本', '把内容生产前置到同一后台', '减少从 0 开始写稿的时间'],
    tone: 'produce',
    cue: '输入选题 / 素材 / 已有内容',
    result: '输出平台可编辑草稿',
  },
  {
    step: '02',
    title: '管理内容',
    desc: '把素材、草稿、成品和待发布内容沉淀为统一内容资产，方便筛选、复用、复盘和继续迭代。',
    points: ['统一内容库与状态流转', '支持多版本持续迭代', '避免素材、草稿、成品分散'],
    tone: 'manage',
    cue: '沉淀草稿 / 素材 / 成品',
    result: '形成统一内容资产库',
  },
  {
    step: '03',
    title: '发布内容',
    desc: '同一份内容映射多个账号与平台，结合定时调度和本地 Agent 执行，稳定完成分发。',
    points: ['多账号多平台统一调度', '登录与执行在绑定设备本地完成', '全程可追踪发布状态'],
    tone: 'publish',
    cue: '映射账号 / 平台 / 时间',
    result: '发起调度与执行追踪',
  },
];

const HERO_ENTRY_POINTS = [
  {
    key: 'produce',
    eyebrow: '推荐起点',
    title: '先进入内容生产台',
    desc: '输入选题、素材或已有内容，直接生成可编辑草稿，再继续进入管理和发布。',
    action: '开始生产',
    target: '/produce',
    featured: true,
  },
  {
    key: 'content',
    eyebrow: '内容管理',
    title: '查看内容库',
    desc: '统一查看草稿、成品和待发布内容，继续筛选、复用和编排。',
    action: '进入内容管理',
    target: '/content',
  },
  {
    key: 'publish',
    eyebrow: '发布调度',
    title: '查看发布计划',
    desc: '进入发布后台，统一安排账号、平台和发布时间。',
    action: '进入发布后台',
    target: '/timedPublish',
  },
];

const FEATURES = [
  {
    title: 'AI 内容生产',
    desc: '围绕选题、来源和目标平台，快速生成图文稿、短视频文案和可继续编辑的草稿，把内容生产放进同一个工作台。',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 3v6" />
        <path d="M9 6h6" />
        <path d="M5 13a7 7 0 1 0 14 0" />
        <path d="m8 15 2 2 6-6" />
      </svg>
    ),
  },
  {
    title: '内容资产管理',
    desc: '把素材、草稿、成品和待发布内容沉淀为统一内容库，支持筛选、复用和版本迭代，减少信息散落。',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M7 8h10" />
        <path d="M7 12h6" />
        <path d="M7 16h8" />
      </svg>
    ),
  },
  {
    title: '多平台发布调度',
    desc: '同一份内容可映射多个账号和平台，支持定时、批量分发和执行追踪，把重复发布动作收敛到一处。',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 12h10" />
        <path d="m10 6 6 6-6 6" />
        <path d="M20 5v14" />
      </svg>
    ),
  },
  {
    title: '本地 Agent 安全执行',
    desc: '账号登录和平台操作由已绑定设备上的本地 Agent 发起，在保持自动化效率的同时更贴近真实使用环境。',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 3l7 4v5c0 5-3.5 7.5-7 9-3.5-1.5-7-4-7-9V7l7-4z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    ),
  },
];

const HIGHLIGHTS = [
  {
    icon: '01',
    title: '不只发内容，而是先把内容产出来',
    desc: 'Publishy 不只是把内容发出去，而是把内容生产、管理、发布串成同一个持续运转的工作流。',
  },
  {
    icon: '02',
    title: '一份源内容，持续衍生多个平台版本',
    desc: '围绕同一主题生成和管理多个平台版本，比每个平台单独重做更适合持续运营。',
  },
  {
    icon: '03',
    title: '本地 Agent 负责登录与执行',
    desc: '账号登录和平台操作在绑定设备本地完成，比纯云端模拟更符合平台环境和稳定性要求。',
  },
  {
    icon: '04',
    title: '从灵感到发布状态全程可追踪',
    desc: '草稿、待发布内容、执行状态和结果统一回到同一后台，方便复盘和继续迭代。',
  },
];

const AUDIENCE_GROUPS = [
  {
    title: '知识型创作者',
    desc: '适合需要持续把观点、研究、经验沉淀成图文或视频，并稳定分发到多个平台的个人创作者。',
  },
  {
    title: '科研与行业内容创作者',
    desc: '适合把论文、行业洞察、课程内容转成大众可传播内容的人群，减少从专业内容到平台内容的转换成本。',
  },
  {
    title: '个人 IP 与小团队',
    desc: '适合 1-10 人的小型内容团队，把生产、管理、发布收敛到同一后台，减少工具切换和协作断点。',
  },
  {
    title: '多账号矩阵运营者',
    desc: '适合已经拥有多个账号，需要在统一节奏下生成内容、沉淀内容库并完成矩阵分发的人。',
  },
];

const HERO_PIPELINE_ITEMS = [
  {
    stage: '生产',
    title: '从一条研究亮点生成图文与视频脚本',
    meta: '已产出 3 个平台版本',
    tone: 'produce',
  },
  {
    stage: '管理',
    title: '草稿入库，进入待发布内容池',
    meta: '12 条内容已归档管理',
    tone: 'manage',
  },
  {
    stage: '发布',
    title: '绑定 Agent 发起登录并执行分发',
    meta: '4 个账号按计划推进',
    tone: 'publish',
  },
];

const HERO_STATUS_ITEMS = [
  {
    platform: '生产内容',
    status: '草稿已生成',
    detail: '3 份',
    tone: 'success',
  },
  {
    platform: '管理内容',
    status: '已入库待编排',
    detail: '12 条',
    tone: 'sync',
  },
  {
    platform: '发布内容',
    status: 'Agent 执行中',
    detail: '4 平台',
    tone: 'pending',
  },
];

const FALLBACK_PUBLIC_PLANS = [
  {
    plan_id: 'trial_3d',
    name: '体验套餐（3天）',
    price_cents: 1990,
    duration_days: 3,
    limits: {
      max_accounts: 2,
      max_tasks: 3,
      monthly_publish_limit: 30,
      max_storage_bytes: 1 * 1024 * 1024 * 1024,
    },
    benefits: {
      credit_grant_mode: 'once',
      credit_grant_amount: 300,
    },
  },
  {
    plan_id: 'creator_1m',
    name: '创作者月会员',
    price_cents: 6900,
    duration_days: 30,
    limits: {
      max_accounts: 5,
      max_tasks: 15,
      monthly_publish_limit: 300,
      max_storage_bytes: 10 * 1024 * 1024 * 1024,
    },
    benefits: {
      credit_grant_mode: 'monthly',
      credit_grant_amount: 1500,
    },
  },
  {
    plan_id: 'creator_1y',
    name: '创作者年会员',
    price_cents: 59900,
    duration_days: 365,
    limits: {
      max_accounts: 10,
      max_tasks: 40,
      monthly_publish_limit: 1000,
      max_storage_bytes: 50 * 1024 * 1024 * 1024,
    },
    benefits: {
      credit_grant_mode: 'monthly',
      credit_grant_amount: 3000,
    },
    recommended: true,
  },
];

const LANDING_CORE_PLAN_IDS = ['trial_3d', 'creator_1m', 'creator_1y'];

const LOGIN_RETURN_TO_KEY = 'publishy_login_return_to';

const normalizeReturnTo = (value) => {
  const s = String(value || '').trim();
  if (!s) return '/content';
  if (!s.startsWith('/')) return '/content';
  if (s.startsWith('//')) return '/content';
  return s;
};

const formatPrice = (cents) => {
  const value = Number(cents || 0) / 100;
  return value % 1 === 0 ? `${value}` : value.toFixed(2);
};

const formatStorage = (bytes) => {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return '0B';
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 * 1024 * 1024)).toFixed(1)}GB`;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)}MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${Math.floor(n)}B`;
};

const resolveDurationLabel = (days) => {
  const d = Number(days || 0);
  if (d >= 36500) return '终身';
  if (d === 3) return '3天';
  if (d === 30) return '1个月';
  if (d === 90) return '3个月';
  if (d === 180) return '6个月';
  if (d === 365) return '1年';
  if (d > 0) return `${d}天`;
  return '-';
};

const planSortWeight = (plan) => {
  const planId = String(plan?.plan_id || '').trim();
  if (planId === 'trial_3d') return 1;
  if (planId === 'creator_1m') return 2;
  if (planId === 'creator_1y') return 3;
  return 100 + Number(plan?.duration_days || 0);
};

const toLandingPricingCard = (plan) => {
  const planId = String(plan?.plan_id || '').trim();
  const duration = resolveDurationLabel(plan?.duration_days);
  const limits = plan?.limits || {};
  const benefits = plan?.benefits || {};
  const displayPriceCents = Number(plan?.effective_price_cents ?? plan?.price_cents ?? 0);
  const publishLimitLabel = planId === 'trial_3d' ? '总发布上限' : '月发布上限';
  const creditGrantText = formatCreditGrantText(benefits);
  const creditEstimateText = formatCreditEstimateSummary(benefits?.credit_grant_amount, { includePrefix: true });
  const featured = Boolean(plan?.recommended) || planId === 'creator_1y';
  const badge = planId === 'trial_3d'
    ? '新手体验'
    : (featured ? '推荐方案' : '');
  return {
    planId,
    key: planId || `${plan?.name || 'plan'}-${plan?.duration_days || 0}`,
    name: plan?.name || planId || '套餐',
    price: `¥${formatPrice(displayPriceCents)}`,
    period: duration === '终身' ? '/终身' : `/${duration}`,
    features: [
      `绑定 ${limits?.max_accounts ?? '-'} 个账号`,
      `最多 ${limits?.max_tasks ?? '-'} 个任务`,
      `${publishLimitLabel} ${limits?.monthly_publish_limit ?? '-'} 次`,
      `存储空间 ${formatStorage(limits?.max_storage_bytes)}`,
      creditGrantText,
      creditEstimateText,
    ].filter(Boolean),
    featured,
    badge,
  };
};

const mergeLandingPlans = (plans) => {
  const incoming = Array.isArray(plans) ? plans.filter(Boolean) : [];
  const byId = new Map();
  incoming.forEach((plan) => {
    const planId = String(plan?.plan_id || '').trim();
    if (planId) byId.set(planId, plan);
  });
  FALLBACK_PUBLIC_PLANS.forEach((fallbackPlan) => {
    const planId = String(fallbackPlan?.plan_id || '').trim();
    if (LANDING_CORE_PLAN_IDS.includes(planId) && !byId.has(planId)) {
      byId.set(planId, fallbackPlan);
    }
  });
  return [...byId.values()].filter((plan) => {
    const planId = String(plan?.plan_id || '').trim();
    return LANDING_CORE_PLAN_IDS.includes(planId);
  });
};

const normalizePlansForLanding = (plans) => {
  const list = mergeLandingPlans(plans);
  list.sort((a, b) => {
    const w = planSortWeight(a) - planSortWeight(b);
    if (w !== 0) return w;
    return Number(a?.duration_days || 0) - Number(b?.duration_days || 0);
  });
  return list.map(toLandingPricingCard);
};

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, refreshMe } = useAuth();
  const [pricingPlans, setPricingPlans] = useState(() => normalizePlansForLanding(FALLBACK_PUBLIC_PLANS));
  const [pricingSourcePlans, setPricingSourcePlans] = useState(() => mergeLandingPlans(FALLBACK_PUBLIC_PLANS));
  const [pricingNote, setPricingNote] = useState('');
  const [actionPlanId, setActionPlanId] = useState('');
  const [campaignCode, setCampaignCode] = useState('');
  const [campaignPreviewLoading, setCampaignPreviewLoading] = useState(false);
  const [campaignPreviewTip, setCampaignPreviewTip] = useState('');
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginReturnTo, setLoginReturnTo] = useState('/content');
  const [checkout, setCheckout] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [payError, setPayError] = useState('');
  const pollRef = useRef(null);
  const pollTokenRef = useRef(0);
  const pollStartAtRef = useRef(0);
  const pollWarnedRef = useRef(false);
  const autoTriggeredPlanRef = useRef('');
  const loginCallbackHandledRef = useRef('');
  const loginQrInitializedRef = useRef(false);

  const wechatCallbackParams = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const code = String(params.get('code') || '').trim();
    const state = String(params.get('state') || '').trim();
    return {
      code: code || '',
      state: state || '',
      hasCallback: Boolean(code && state),
    };
  }, [location.search]);

  const queryReturnTo = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return normalizeReturnTo(params.get('return_to'));
  }, [location.search]);

  const persistLoginReturnTo = useCallback((returnTo) => {
    const safeReturnTo = normalizeReturnTo(returnTo);
    setLoginReturnTo(safeReturnTo);
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.setItem(LOGIN_RETURN_TO_KEY, safeReturnTo);
    }
    return safeReturnTo;
  }, []);

  const resolveLoginReturnTo = useCallback(() => {
    const fromQuery = normalizeReturnTo(queryReturnTo);
    if (fromQuery && fromQuery !== '/content') return fromQuery;
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const stored = normalizeReturnTo(window.sessionStorage.getItem(LOGIN_RETURN_TO_KEY));
      if (stored && stored !== '/content') return stored;
    }
    return normalizeReturnTo(loginReturnTo);
  }, [loginReturnTo, queryReturnTo]);

  const clearLoginReturnTo = useCallback(() => {
    setLoginReturnTo('/content');
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(LOGIN_RETURN_TO_KEY);
    }
  }, []);

  const pricingReminderText = useMemo(
    () => `提供 ${pricingPlans.length} 种核心会员方案，覆盖体验、月会员与年会员；先开通会员，再按需补充积分，理解更直接。`,
    [pricingPlans.length],
  );

  const initLoginQr = useCallback(async (returnTo) => {
    const safeReturnTo = persistLoginReturnTo(returnTo);
    setLoginLoading(true);
    setLoginError('');
    try {
      const url = `${API_BASE_URL}/auth/wechat/qr_params?return_to=${encodeURIComponent(safeReturnTo)}`;
      const resp = await fetch(url, { method: 'GET', credentials: 'include' });
      if (!resp.ok) throw new Error(`获取扫码参数失败（${resp.status}）`);
      const contentType = String(resp.headers.get('content-type') || '');
      if (!contentType.includes('application/json')) {
        const text = await resp.text();
        throw new Error(`获取扫码参数返回非 JSON：${text.slice(0, 120)}`);
      }
      const data = await resp.json();
      if (data?.status !== 'success') throw new Error(resolveErrorMessage(data?.error_message, '获取扫码参数失败'));
      if (!data?.appid) throw new Error('后端未配置 WECHAT_OPEN_APPID，无法生成二维码');
      await renderWxLogin({
        containerId: 'home_wx_login_qr',
        appid: data.appid,
        scope: data.scope,
        redirectUri: data.redirect_uri,
        state: data.state,
        fastLogin: 1,
        styleLite: 1,
      });
    } catch (e) {
      setLoginError(e?.message || String(e));
    } finally {
      setLoginLoading(false);
    }
  }, [persistLoginReturnTo]);

  const completeLogin = useCallback(async ({ code, state }) => {
    setLoginLoading(true);
    setLoginError('');
    try {
      const returnTo = resolveLoginReturnTo();
      const resp = await fetch(`${API_BASE_URL}/auth/wechat/complete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channel: 'open',
          code,
          state,
          return_to: returnTo,
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`登录失败（${resp.status}）：${text.slice(0, 180)}`);
      }
      const data = await resp.json();
      if (data?.status !== 'success') throw new Error(resolveErrorMessage(data?.error_message, '登录失败'));
      await refreshMe({ silent: true });
      setLoginOpen(false);
      clearLoginReturnTo();
      navigate(data?.return_to || returnTo, { replace: true });
    } catch (e) {
      setLoginError(e?.message || String(e));
      setLoginOpen(true);
      loginQrInitializedRef.current = false;
    } finally {
      setLoginLoading(false);
    }
  }, [clearLoginReturnTo, navigate, refreshMe, resolveLoginReturnTo]);

  const handleStartExperience = useCallback((returnTo = '/content') => {
    if (authLoading) {
      message.info('正在确认登录状态，请稍后重试');
      return;
    }
    if (user) {
      navigate(normalizeReturnTo(returnTo));
      return;
    }
    const safeReturnTo = persistLoginReturnTo(returnTo);
    setLoginError('');
    setLoginOpen(true);
    setLoginReturnTo(safeReturnTo);
    loginQrInitializedRef.current = false;
  }, [authLoading, navigate, persistLoginReturnTo, user]);

  const stopPolling = useCallback(() => {
    pollTokenRef.current += 1;
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollStartAtRef.current = 0;
    pollWarnedRef.current = false;
  }, []);

  const handlePaymentSuccess = useCallback(() => {
    stopPolling();
    setQrOpen(false);
    setCheckout(null);
    setPayError('');
    message.success('支付成功，订阅已生效');
  }, [stopPolling]);

  const startPolling = useCallback((orderNos = []) => {
    stopPolling();
    const pendingOrders = Array.from(new Set(
      (Array.isArray(orderNos) ? orderNos : [orderNos])
        .map((item) => String(item || '').trim())
        .filter(Boolean),
    ));
    if (pendingOrders.length === 0) return;
    const currentToken = pollTokenRef.current;
    pollStartAtRef.current = Date.now();
    pollWarnedRef.current = false;

    pollRef.current = setInterval(async () => {
      if (currentToken !== pollTokenRef.current) return;
      for (const outTradeNo of pendingOrders) {
        try {
          const reconcile = await reconcilePaymentStatus(outTradeNo);
          if (currentToken !== pollTokenRef.current) return;
          const tradeState = String(reconcile?.trade_state || '').toUpperCase();
          if (
            reconcile?.status === 'success'
            && ['SUCCESS', 'TRADE_SUCCESS', 'TRADE_FINISHED'].includes(tradeState)
          ) {
            handlePaymentSuccess();
            return;
          }
        } catch (_) {
          // ignore polling errors
        }
      }
      if (!pollWarnedRef.current && pollStartAtRef.current > 0 && Date.now() - pollStartAtRef.current > 8 * 60 * 1000) {
        pollWarnedRef.current = true;
        message.warning('支付结果同步较慢，可稍后查看订阅状态。');
      }
    }, 5000);
  }, [handlePaymentSuccess, stopPolling]);

  const triggerCheckout = useCallback(async (plan) => {
    const planId = String(plan?.planId || '').trim();
    if (!planId) return;
    if (authLoading) {
      message.info('正在确认登录状态，请稍后重试');
      return;
    }

    if (!user) {
      const returnTo = `/?buy_plan=${encodeURIComponent(planId)}#pricing`;
      handleStartExperience(returnTo);
      return;
    }

    setActionPlanId(planId);
    setPayError('');
    try {
      const sourcePlan = pricingSourcePlans.find((item) => String(item?.plan_id || '').trim() === planId) || null;
      const channelDefs = [
        { key: 'wechat', code: 'wx_native', label: '微信支付', missingMsg: '未获取到微信二维码，请稍后重试' },
        { key: 'alipay', code: 'alipay_pc', label: '支付宝支付', missingMsg: '未获取到支付宝二维码，请稍后重试' },
      ];
      const settled = await Promise.allSettled(
        channelDefs.map((channel) => createCheckout({
          plan_id: planId,
          channel_code: channel.code,
          campaign_code: String(campaignCode || '').trim() || undefined,
        })),
      );

      const payments = {};
      const orderNos = [];
      const channelErrors = [];
      let resolvedPlan = sourcePlan;
      let resolvedCampaign = null;

      settled.forEach((result, idx) => {
        const channel = channelDefs[idx];
        if (result.status !== 'fulfilled') {
          channelErrors.push(`${channel.label}：${result.reason?.message || String(result.reason || '创建失败')}`);
          return;
        }
        const resp = result.value || {};
        if (resp?.status !== 'success') {
          channelErrors.push(`${channel.label}：${resolveErrorMessage(resp?.error_message, '创建支付失败')}`);
          return;
        }
        const qrValue = String(resp?.display_content || resp?.code_url || '').trim();
        if (!qrValue) {
          channelErrors.push(`${channel.label}：${channel.missingMsg}`);
          return;
        }
        const outTradeNo = String(resp?.out_trade_no || '').trim();
        if (outTradeNo) orderNos.push(outTradeNo);
        if (!resolvedPlan && resp?.plan) resolvedPlan = resp.plan;
        if (resp?.campaign) resolvedCampaign = resp.campaign;
        payments[channel.key] = {
          out_trade_no: outTradeNo,
          channel_code: channel.code,
          display_mode: 'qr_code',
          display_content: qrValue,
          label: channel.label,
        };
      });

      if (!payments.wechat && !payments.alipay) {
        throw new Error(channelErrors.join('；') || '创建支付失败');
      }

      setCheckout({
        channel_code: 'multi_qr',
        display_mode: 'multi_qr',
        plan_name: plan?.name || '',
        plan: resolvedPlan || null,
        campaign: resolvedCampaign,
        payments,
        order_nos: orderNos,
        out_trade_no: orderNos[0] || '',
      });
      setPayError(channelErrors.length > 0 ? `部分支付通道不可用：${channelErrors.join('；')}` : '');
      setQrOpen(true);
      startPolling(orderNos);
    } catch (e) {
      setPayError(e?.message || String(e));
    } finally {
      setActionPlanId('');
    }
  }, [authLoading, campaignCode, handleStartExperience, pricingSourcePlans, startPolling, user]);

  const handlePreviewCampaign = useCallback(async () => {
    if (authLoading) {
      message.info('正在确认登录状态，请稍后重试');
      return;
    }
    if (!user) {
      message.info('请先登录后查看折扣价');
      handleStartExperience('/#pricing');
      return;
    }
    setCampaignPreviewLoading(true);
    setCampaignPreviewTip('');
    try {
      const resp = await previewBillingPlans({ campaign_code: String(campaignCode || '').trim() });
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.error_message, '折扣预览失败'));
      }
      const plans = mergeLandingPlans(Array.isArray(resp?.plans) && resp.plans.length > 0 ? resp.plans : FALLBACK_PUBLIC_PLANS);
      setPricingSourcePlans(plans);
      setPricingPlans(normalizePlansForLanding(plans));
      setPricingNote('');
      const preview = resp?.preview || {};
      if (preview?.applied) {
        const tip = `已应用渠道优惠，命中 ${Number(preview?.applied_plan_count || 0)} 个套餐`;
        setCampaignPreviewTip(tip);
        message.success(tip);
      } else if (preview?.reset) {
        setCampaignPreviewTip('已恢复标准价格');
      } else {
        const tip = resolveErrorMessage(preview?.error_message, '该渠道码未命中当前套餐，价格未变化');
        setCampaignPreviewTip(tip);
        message.info(tip);
      }
    } catch (e) {
      setPricingNote(e?.message || String(e));
    } finally {
      setCampaignPreviewLoading(false);
    }
  }, [authLoading, campaignCode, handleStartExperience, user]);

  useEffect(() => {
    document.body.classList.add('marketing-home-body');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.12,
      },
    );

    const revealEls = document.querySelectorAll('.home-reveal');
    revealEls.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
      document.body.classList.remove('marketing-home-body');
    };
  }, []);

  useEffect(() => {
    let canceled = false;
    const run = async () => {
      try {
        const resp = await getPublicPlans();
        if (resp?.status !== 'success') {
          throw new Error(resolveErrorMessage(resp?.error_message, '获取套餐失败'));
        }
        const normalizedPlans = normalizePlansForLanding(resp?.plans);
        if (normalizedPlans.length === 0) {
          throw new Error('公开套餐列表为空');
        }
        if (!canceled) {
          setPricingPlans(normalizedPlans);
          setPricingSourcePlans(mergeLandingPlans(resp?.plans));
          setPricingNote('');
        }
      } catch (e) {
        if (!canceled) {
          setPricingPlans(normalizePlansForLanding(FALLBACK_PUBLIC_PLANS));
          setPricingSourcePlans(mergeLandingPlans(FALLBACK_PUBLIC_PLANS));
          setPricingNote(`价格同步异常，当前显示默认套餐：${e?.message || String(e)}`);
        }
      }
    };
    run().catch(() => {});
    return () => {
      canceled = true;
    };
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const buyPlan = String(params.get('buy_plan') || '').trim();
    if (!buyPlan || !user) return;
    if (autoTriggeredPlanRef.current === buyPlan) return;
    const targetPlan = pricingPlans.find((p) => String(p.planId || '') === buyPlan);
    if (!targetPlan) return;
    autoTriggeredPlanRef.current = buyPlan;
    triggerCheckout(targetPlan).catch(() => {});

    const nextParams = new URLSearchParams(location.search || '');
    nextParams.delete('buy_plan');
    const nextSearch = nextParams.toString();
    const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${location.hash || '#pricing'}`;
    navigate(nextUrl, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate, pricingPlans, triggerCheckout, user]);

  useEffect(() => {
    if (user && loginOpen) {
      setLoginOpen(false);
    }
  }, [loginOpen, user]);

  useEffect(() => {
    if (user || !loginOpen || wechatCallbackParams.hasCallback) return;
    if (loginQrInitializedRef.current) return;
    loginQrInitializedRef.current = true;
    initLoginQr(loginReturnTo).catch((e) => {
      setLoginError(e?.message || String(e));
      loginQrInitializedRef.current = false;
    });
  }, [initLoginQr, loginOpen, loginReturnTo, user, wechatCallbackParams.hasCallback]);

  useEffect(() => {
    if (user) return;
    const params = new URLSearchParams(location.search || '');
    if (params.get('login') !== '1') return;
    const returnTo = normalizeReturnTo(params.get('return_to'));
    const safeReturnTo = persistLoginReturnTo(returnTo);
    setLoginReturnTo(safeReturnTo);
    setLoginOpen(true);
    loginQrInitializedRef.current = false;
  }, [location.search, persistLoginReturnTo, user]);

  useEffect(() => {
    if (!wechatCallbackParams.hasCallback) return;
    const callbackKey = `${wechatCallbackParams.code}:${wechatCallbackParams.state}`;
    if (loginCallbackHandledRef.current === callbackKey) return;
    loginCallbackHandledRef.current = callbackKey;
    setLoginOpen(true);

    const params = new URLSearchParams(location.search || '');
    params.delete('code');
    params.delete('state');
    const cleaned = params.toString();
    const cleanedUrl = `${location.pathname}${cleaned ? `?${cleaned}` : ''}${location.hash || ''}`;
    navigate(cleanedUrl, { replace: true });

    completeLogin({
      code: wechatCallbackParams.code,
      state: wechatCallbackParams.state,
    }).catch((e) => setLoginError(e?.message || String(e)));
  }, [
    completeLogin,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    wechatCallbackParams.code,
    wechatCallbackParams.hasCallback,
    wechatCallbackParams.state,
  ]);

  return (
    <div className="marketing-home">
      <nav className="home-nav">
        <div className="home-container">
          <div className="home-logo">
            <img src="/logo.svg" alt="Publishy Logo" className="home-logo-image" />
            Publishy
          </div>
          <div className="home-nav-links">
            <a href="#workflow">产品介绍</a>
            <a href="#features">功能特性</a>
            <a href="#audience">适用人群</a>
            <a href="#pricing">订阅价格</a>
          </div>
          <Link
            to="#"
            onClick={(e) => {
              e.preventDefault();
              handleStartExperience('/produce');
            }}
            className="home-nav-btn home-btn-ghost"
          >
            立即体验
          </Link>
        </div>
      </nav>

      <section className="home-hero">
        <div className="home-orb home-orb-1" />
        <div className="home-orb home-orb-2" />
        <div className="home-container">
          <div className="home-hero-grid">
            <div className="home-hero-content home-reveal">
              <span className="home-hero-kicker">AI 内容生产、管理与发布平台</span>
              <h1>
                <span className="home-text-gradient">从内容生产</span>
                <br />
                到多平台发布，一个后台完成
              </h1>
              <p>先生成内容，再沉淀为内容资产，最后通过已绑定 Agent 安全登录多平台完成发布。Publishy 不只是帮你发内容，而是把生产、管理、发布放进同一个产品里。</p>
              <div className="home-hero-pill-row">
                <span className="home-hero-pill">生产内容</span>
                <span className="home-hero-pill">管理内容</span>
                <span className="home-hero-pill">发布内容</span>
              </div>
              <div className="home-hero-actions">
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleStartExperience('/produce');
                  }}
                  className="home-hero-btn home-primary"
                >
                  进入内容生产台
                </Link>
                <a href="#workflow" className="home-hero-btn home-secondary">查看产品介绍</a>
              </div>
              <div className="home-hero-entry-grid">
                {HERO_ENTRY_POINTS.map((item) => (
                  <article
                    key={item.key}
                    className={`home-hero-entry-card${item.featured ? ' home-hero-entry-card-featured' : ''}`}
                  >
                    <span className="home-hero-entry-eyebrow">{item.eyebrow}</span>
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                    <Link
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleStartExperience(item.target);
                      }}
                      className={`home-hero-entry-link${item.featured ? ' featured' : ''}`}
                    >
                      {item.action}
                    </Link>
                  </article>
                ))}
              </div>
              <div className="home-hero-entry-tip">
                推荐路径：先生产内容，再进入内容库继续整理，最后安排多平台发布。
              </div>
            </div>

            <div className="home-hero-visual home-reveal">
              <div className="home-dashboard-card">
                <div className="home-db-header">
                  <div>
                    <span className="home-db-dot home-red" />
                    <span className="home-db-dot home-yellow" />
                    <span className="home-db-dot home-green" />
                  </div>
                  <span className="home-db-mode">内容工作流面板</span>
                </div>
                {HERO_PIPELINE_ITEMS.map((item) => (
                  <div key={item.stage} className={`home-db-stage home-db-stage-${item.tone}`}>
                    <div className="home-db-stage-head">
                      <span className={`home-db-stage-badge home-db-stage-badge-${item.tone}`}>{item.stage}</span>
                      <span className="home-db-stage-meta">{item.meta}</span>
                    </div>
                    <div className="home-db-stage-title">{item.title}</div>
                  </div>
                ))}
              </div>
              <div className="home-hero-status-wrap">
                <div className="home-hero-status-card">
                  <div className="home-hero-status-head">
                    <span>流程进度</span>
                    <span>3/3</span>
                  </div>
                  <div className="home-hero-progress-track">
                    <div className="home-hero-progress-fill" />
                  </div>
                  <div className="home-hero-status-list">
                    {HERO_STATUS_ITEMS.map((item) => (
                      <div key={item.platform} className="home-hero-status-item">
                        <span className={`home-hero-status-dot home-hero-dot-${item.tone}`} />
                        <span className="home-hero-status-platform">{item.platform}</span>
                        <span className="home-hero-status-text">{item.status}</span>
                        <span className="home-hero-status-detail">{item.detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="home-platform-strip">
        <div className="home-marquee-container">
          <div className="home-marquee-track">
            {PLATFORM_TRACK_ITEMS.map((name, index) => (
              <div key={`track-a-${name}-${index}`} className="home-platform-item">{name}</div>
            ))}
          </div>
          <div className="home-marquee-track" aria-hidden="true">
            {PLATFORM_TRACK_ITEMS.map((name, index) => (
              <div key={`track-b-${name}-${index}`} className="home-platform-item">{name}</div>
            ))}
          </div>
        </div>
      </section>

      <section id="workflow" className="home-section-workflow">
        <div className="home-container">
          <div className="home-section-header home-reveal">
            <span className="home-section-tag">产品介绍</span>
            <h2 className="home-section-title">从内容生产到发布，三步完成</h2>
            <p>Publishy 把内容生产、内容管理和多平台发布整合到同一后台，让内容从生成到分发连续推进。</p>
          </div>
          <div className="home-workflow-grid">
            {WORKFLOW_STEPS.map((item, index) => (
              <article
                key={item.step}
                className={`home-workflow-card home-workflow-card-${item.tone} home-reveal`}
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="home-workflow-card-head">
                  <div className="home-workflow-step">{item.step}</div>
                  <span className={`home-workflow-cue home-workflow-cue-${item.tone}`}>{item.cue}</span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
                <ul className="home-workflow-points">
                  {item.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
                <div className="home-workflow-card-foot">
                  <span className="home-workflow-result-label">阶段结果</span>
                  <span className={`home-workflow-result home-workflow-result-${item.tone}`}>{item.result}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="home-section-features">
        <div className="home-container">
          <div className="home-section-header home-reveal">
            <span className="home-section-tag">功能特性</span>
            <h2 className="home-section-title">围绕内容工作流设计的四个核心能力</h2>
            <p>不是把功能堆在一起，而是让内容在同一后台持续生产、管理和发布。</p>
          </div>
          <div className="home-features-grid">
            {FEATURES.map((feature, index) => (
              <article
                key={feature.title}
                className="home-feature-card home-reveal"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="home-feature-icon-wrapper">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="highlights" className="home-section-highlights">
        <div className="home-container">
          <div className="home-section-header home-reveal">
            <h2 className="home-section-title">为什么 Publishy 不只是一个发布工具</h2>
            <p>真正的差异化不在“能发出去”，而在“能持续产出、沉淀和复用内容”。</p>
          </div>
          <div className="home-highlight-grid">
            {HIGHLIGHTS.map((item, index) => (
              <article
                key={item.title}
                className="home-highlight-item home-reveal"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="home-h-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="audience" className="home-section-audience">
        <div className="home-container">
          <div className="home-section-header home-reveal">
            <span className="home-section-tag">适用人群</span>
            <h2 className="home-section-title">更适合需要持续产出内容的人</h2>
            <p>尤其适合需要一边生产内容、一边管理内容、一边做多平台分发的创作者和小团队。</p>
          </div>
          <div className="home-audience-grid">
            {AUDIENCE_GROUPS.map((item, index) => (
              <article
                key={item.title}
                className="home-audience-card home-reveal"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="home-section-pricing">
        <div className="home-container">
          <div className="home-section-header home-reveal">
            <span className="home-section-tag">订阅方案</span>
            <h2 className="home-section-title">灵活的定价策略</h2>
            <p className="home-pricing-tip">{pricingReminderText}</p>
            {pricingNote ? <p className="home-pricing-error">{pricingNote}</p> : null}
            <div className="home-pricing-campaign-box">
              <Typography.Text type="secondary">渠道码（可选）</Typography.Text>
              <div className="home-pricing-campaign-row">
                <Input
                  value={campaignCode}
                  onChange={(e) => setCampaignCode(e.target.value)}
                  placeholder="输入渠道码后，点击“折扣价”更新套餐价格"
                  maxLength={64}
                  onPressEnter={() => {
                    if (!campaignPreviewLoading) handlePreviewCampaign();
                  }}
                />
                <button
                  type="button"
                  className="home-btn-outline home-campaign-preview-btn"
                  onClick={() => {
                    if (!campaignPreviewLoading) handlePreviewCampaign();
                  }}
                  disabled={campaignPreviewLoading}
                >
                  {campaignPreviewLoading ? '计算中...' : '折扣价'}
                </button>
              </div>
              {campaignPreviewTip ? <Typography.Text type="secondary">{campaignPreviewTip}</Typography.Text> : null}
            </div>
          </div>
          <div className="home-pricing-grid">
            {pricingPlans.map((plan, index) => (
              <article
                key={plan.key}
                className={`home-price-card${plan.featured ? ' home-featured' : ''} home-reveal`}
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                {plan.badge ? <div className="home-featured-badge">{plan.badge}</div> : null}
                <div className="home-plan-name">{plan.name}</div>
                <div className="home-plan-price">
                  {plan.price}
                  <span>{plan.period}</span>
                </div>
                <ul className="home-plan-features">
                  {plan.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    triggerCheckout(plan).catch(() => {});
                  }}
                  className={`home-btn-block ${plan.featured ? 'home-btn-primary' : 'home-btn-outline'}`}
                >
                  {actionPlanId === plan.planId ? '正在创建订单...' : '扫码支付'}
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <footer id="about" className="home-footer">
        <div className="home-container">
          <div className="home-footer-grid">
            <div className="home-footer-brand">
              <div className="home-footer-brand-title">
                <img className="home-footer-brand-logo" src="/logo.svg" alt="Publishy Logo" />
                <h4>Publishy</h4>
              </div>
              <p>把内容生产、内容管理和多平台发布收敛到同一后台，帮助创作者把灵感真正变成持续输出。</p>
            </div>
            <div className="home-footer-col">
              <h5>产品</h5>
              <a href="#workflow">产品介绍</a>
              <a href="#features">功能特性</a>
              <a href="#pricing">订阅价格</a>
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleStartExperience('/produce');
                }}
              >
                立即体验
              </Link>
            </div>
            <div className="home-footer-col">
              <h5>公司</h5>
              <a href="/about.html" target="_blank" rel="noopener noreferrer">关于我们</a>
              <a href="/service-protocol.html" target="_blank" rel="noopener noreferrer">服务协议</a>
              <a href="/privacy-protocol.html" target="_blank" rel="noopener noreferrer">隐私条款</a>
            </div>
            <div className="home-footer-col home-contact-col">
              <h5>联系我们</h5>
              <img className="home-qr-box" src="/contact.png" alt="Publishy 联系二维码" />
            </div>
          </div>
          <div className="home-copyright">
            © 2026 Publishy. All rights reserved.
          </div>
        </div>
      </footer>

      <Modal
        title="微信登录"
        open={loginOpen}
        onCancel={() => {
          setLoginOpen(false);
          setLoginError('');
          loginQrInitializedRef.current = false;
        }}
        footer={null}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          请使用微信扫码登录后立即体验 Publishy。
        </Typography.Paragraph>
        {loginError ? (
          <Alert
            type="error"
            showIcon
            message="登录初始化失败"
            description={(
              <>
                <div>{loginError}</div>
                <div style={{ marginTop: 8 }}>
                  <a
                    href={`${API_BASE_URL}/auth/wechat/login?channel=open&return_to=${encodeURIComponent(resolveLoginReturnTo())}`}
                    rel="noopener noreferrer"
                  >
                    改用跳转登录
                  </a>
                </div>
              </>
            )}
            style={{ marginBottom: 12 }}
          />
        ) : null}
        <Spin spinning={loginLoading}>
          <div className="home-wx-login-wrap">
            <div id="home_wx_login_qr" className="home-wx-login" />
          </div>
        </Spin>
      </Modal>

      <CheckoutPaymentModal
        title="扫码支付"
        open={qrOpen}
        onCancel={() => {
          setQrOpen(false);
          stopPolling();
        }}
        checkout={checkout}
        allPlans={pricingSourcePlans}
        qrcodeSize={180}
        errorMessage={payError}
      />
    </div>
  );
};

export default LandingPage;
